import { useMemo, useState } from "react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CheckCircle2, Copy, ExternalLink, MessageCircle, Phone, Calendar, CalendarClock, Mail, Send } from "lucide-react";
import { toast } from "sonner";

type Step = { title: string; title_bn?: string; body: string; body_bn?: string; code?: string };
type Integration = {
  id: string;
  name: string;
  name_bn: string;
  category: "messaging" | "scheduling" | "email";
  icon: React.ReactNode;
  tagline: string;
  tagline_bn: string;
  difficulty: "Easy" | "Medium" | "Advanced";
  timeMinutes: number;
  docsUrl: string;
  steps: Step[];
  envVars?: { name: string; hint: string; hint_bn: string }[];
};

const INTEGRATIONS: Integration[] = [
  {
    id: "whatsapp-cloud",
    name: "WhatsApp Cloud API (Meta)",
    name_bn: "হোয়াটসঅ্যাপ ক্লাউড API (মেটা)",
    category: "messaging",
    icon: <MessageCircle className="h-5 w-5" />,
    tagline: "Official Meta WhatsApp Business Cloud — best for scale.",
    tagline_bn: "মেটার অফিসিয়াল হোয়াটসঅ্যাপ বিজনেস ক্লাউড — বড় ভলিউমের জন্য সেরা।",
    difficulty: "Medium",
    timeMinutes: 20,
    docsUrl: "https://developers.facebook.com/docs/whatsapp/cloud-api",
    envVars: [
      { name: "META_WHATSAPP_TOKEN", hint: "Permanent System User access token", hint_bn: "স্থায়ী সিস্টেম ইউজার টোকেন" },
      { name: "META_WHATSAPP_PHONE_ID", hint: "Phone number ID from Meta app", hint_bn: "মেটা অ্যাপ থেকে ফোন নম্বর আইডি" },
      { name: "META_VERIFY_TOKEN", hint: "Any random string you choose", hint_bn: "আপনার পছন্দের যেকোনো র‍্যান্ডম স্ট্রিং" },
    ],
    steps: [
      { title: "Create a Meta Business account", title_bn: "মেটা বিজনেস অ্যাকাউন্ট তৈরি করুন", body: "Go to business.facebook.com and set up your clinic as a business.", body_bn: "business.facebook.com এ যান এবং আপনার ক্লিনিককে বিজনেস হিসেবে সেটআপ করুন।" },
      { title: "Create a WhatsApp app", title_bn: "হোয়াটসঅ্যাপ অ্যাপ তৈরি করুন", body: "In Meta for Developers → My Apps → Create App → Business → WhatsApp. Add a test phone number to start.", body_bn: "Meta for Developers → My Apps → Create App → Business → WhatsApp। শুরুতে টেস্ট ফোন নম্বর যোগ করুন।" },
      { title: "Generate a permanent token", title_bn: "স্থায়ী টোকেন জেনারেট করুন", body: "Business Settings → System Users → Add — grant whatsapp_business_messaging + whatsapp_business_management scopes.", body_bn: "Business Settings → System Users → Add — whatsapp_business_messaging + whatsapp_business_management অনুমতি দিন।" },
      { title: "Add secrets in Doctorsx", title_bn: "Doctorsx-এ সিক্রেট যোগ করুন", body: "Open Settings → Integrations → WhatsApp Cloud and paste META_WHATSAPP_TOKEN, META_WHATSAPP_PHONE_ID, META_VERIFY_TOKEN.", body_bn: "Settings → Integrations → WhatsApp Cloud খুলুন এবং টোকেনগুলো পেস্ট করুন।" },
      { title: "Set webhook URL", title_bn: "ওয়েবহুক URL সেট করুন", body: "Copy your Doctorsx webhook URL (Settings → Integrations) and paste it into Meta → WhatsApp → Configuration → Webhook. Subscribe to messages, message_status.", body_bn: "Doctorsx-এর ওয়েবহুক URL কপি করে Meta-এর Configuration-এ পেস্ট করুন। messages ও message_status সাবস্ক্রাইব করুন।", code: "https://YOUR-PROJECT.supabase.co/functions/v1/whatsapp-agent" },
      { title: "Send a test message", title_bn: "টেস্ট মেসেজ পাঠান", body: "Message your business number from your personal WhatsApp. The AI Agent Inbox should show it within seconds.", body_bn: "আপনার পার্সোনাল হোয়াটসঅ্যাপ থেকে বিজনেস নম্বরে মেসেজ পাঠান — AI Agent Inbox-এ কয়েক সেকেন্ডে দেখা যাবে।" },
    ],
  },
  {
    id: "twilio-whatsapp",
    name: "Twilio WhatsApp",
    name_bn: "টুইলিও হোয়াটসঅ্যাপ",
    category: "messaging",
    icon: <MessageCircle className="h-5 w-5" />,
    tagline: "Fastest sandbox — great for pilots and small clinics.",
    tagline_bn: "সবচেয়ে দ্রুত স্যান্ডবক্স — পাইলট ও ছোট ক্লিনিকের জন্য উত্তম।",
    difficulty: "Easy",
    timeMinutes: 10,
    docsUrl: "https://www.twilio.com/docs/whatsapp/quickstart",
    envVars: [
      { name: "TWILIO_ACCOUNT_SID", hint: "From Twilio Console dashboard", hint_bn: "টুইলিও কনসোল ড্যাশবোর্ড থেকে" },
      { name: "TWILIO_AUTH_TOKEN", hint: "Rotate anytime from console", hint_bn: "যেকোনো সময় কনসোল থেকে রোটেট করা যায়" },
      { name: "TWILIO_WHATSAPP_FROM", hint: "e.g. whatsapp:+14155238886", hint_bn: "উদাহরণ: whatsapp:+14155238886" },
    ],
    steps: [
      { title: "Sign up at twilio.com", title_bn: "twilio.com-এ সাইন আপ করুন", body: "Free trial gives you $15 credit — enough for hundreds of test messages.", body_bn: "ফ্রি ট্রায়ালে $15 ক্রেডিট পাওয়া যায় — শত শত টেস্ট মেসেজের জন্য যথেষ্ট।" },
      { title: "Join the WhatsApp sandbox", title_bn: "হোয়াটসঅ্যাপ স্যান্ডবক্সে যুক্ত হন", body: "Messaging → Try it out → WhatsApp — follow the 'join <code-word>' instruction from your phone.", body_bn: "Messaging → Try it out → WhatsApp — ফোন থেকে 'join <code-word>' পাঠান।" },
      { title: "Copy your credentials", title_bn: "ক্রেডেনশিয়াল কপি করুন", body: "Grab Account SID, Auth Token, and your sandbox 'From' number.", body_bn: "Account SID, Auth Token ও স্যান্ডবক্স 'From' নম্বর নিন।" },
      { title: "Paste into Doctorsx", title_bn: "Doctorsx-এ পেস্ট করুন", body: "Settings → Integrations → Twilio WhatsApp. Save. Send a test from the panel.", body_bn: "Settings → Integrations → Twilio WhatsApp. সেভ করে প্যানেল থেকে টেস্ট পাঠান।" },
      { title: "Configure inbound webhook", title_bn: "ইনবাউন্ড ওয়েবহুক সেটআপ করুন", body: "Twilio → WhatsApp Sandbox → 'When a message comes in' → paste the Doctorsx webhook URL.", body_bn: "Twilio → WhatsApp Sandbox → 'When a message comes in' → Doctorsx ওয়েবহুক URL পেস্ট করুন।", code: "https://YOUR-PROJECT.supabase.co/functions/v1/whatsapp-agent" },
      { title: "Upgrade to production number", title_bn: "প্রোডাকশন নম্বরে আপগ্রেড", body: "Once approved by WhatsApp, replace the sandbox 'From' with your business number.", body_bn: "হোয়াটসঅ্যাপ অনুমোদন পেলে স্যান্ডবক্স নম্বর বদলে বিজনেস নম্বর দিন।" },
    ],
  },
  {
    id: "twilio-sms",
    name: "Twilio SMS",
    name_bn: "টুইলিও এসএমএস",
    category: "messaging",
    icon: <Phone className="h-5 w-5" />,
    tagline: "Fallback SMS when patients don't have WhatsApp.",
    tagline_bn: "রোগীর হোয়াটসঅ্যাপ না থাকলে ফলব্যাক এসএমএস।",
    difficulty: "Easy",
    timeMinutes: 8,
    docsUrl: "https://www.twilio.com/docs/sms/quickstart",
    envVars: [
      { name: "TWILIO_ACCOUNT_SID", hint: "Same as WhatsApp integration", hint_bn: "হোয়াটসঅ্যাপ ইন্টিগ্রেশনের মতোই" },
      { name: "TWILIO_AUTH_TOKEN", hint: "Same as WhatsApp integration", hint_bn: "হোয়াটসঅ্যাপ ইন্টিগ্রেশনের মতোই" },
      { name: "TWILIO_SMS_FROM", hint: "Your purchased phone number", hint_bn: "কেনা ফোন নম্বর" },
    ],
    steps: [
      { title: "Buy a phone number", title_bn: "ফোন নম্বর কিনুন", body: "Twilio Console → Phone Numbers → Buy. Choose SMS-enabled Bangladesh or virtual number.", body_bn: "Twilio Console → Phone Numbers → Buy. SMS-সাপোর্টেড বাংলাদেশ বা ভার্চুয়াল নম্বর বেছে নিন।" },
      { title: "Enable geo permissions", title_bn: "জিও অনুমতি চালু করুন", body: "Programmable Messaging → Geo Permissions → allow Bangladesh (mandatory).", body_bn: "Programmable Messaging → Geo Permissions → Bangladesh অনুমোদন করুন (আবশ্যক)।" },
      { title: "Turn on SMS Pumping Protection", title_bn: "SMS Pumping Protection চালু করুন", body: "Prevents fraud that could burn your balance.", body_bn: "ফ্রড থেকে আপনার ব্যালেন্স রক্ষা করবে।" },
      { title: "Save credentials in Doctorsx", title_bn: "Doctorsx-এ ক্রেডেনশিয়াল সেভ", body: "Settings → Integrations → SMS. Paste SID, token, and 'From' number.", body_bn: "Settings → Integrations → SMS-এ SID, token ও 'From' পেস্ট করুন।" },
      { title: "Send a test SMS", title_bn: "টেস্ট SMS পাঠান", body: "Use the built-in tester. Check delivery report inside Communications page.", body_bn: "বিল্ট-ইন টেস্টার দিয়ে পাঠান — Communications পেজে ডেলিভারি রিপোর্ট দেখুন।" },
    ],
  },
  {
    id: "google-calendar",
    name: "Google Calendar",
    name_bn: "গুগল ক্যালেন্ডার",
    category: "scheduling",
    icon: <Calendar className="h-5 w-5" />,
    tagline: "Two-way sync so appointments appear on your phone.",
    tagline_bn: "টু-ওয়ে সিঙ্ক — অ্যাপয়েন্টমেন্ট আপনার ফোনেও দেখা যাবে।",
    difficulty: "Easy",
    timeMinutes: 5,
    docsUrl: "https://developers.google.com/calendar",
    steps: [
      { title: "Open Calendar Sync page", title_bn: "Calendar Sync পেজ খুলুন", body: "In Doctorsx sidebar → Calendar Sync.", body_bn: "সাইডবার → Calendar Sync।" },
      { title: "Click Connect Google", title_bn: "Connect Google-এ ক্লিক", body: "OAuth popup — sign in with the account you use daily.", body_bn: "OAuth পপআপে সাইন ইন করুন।" },
      { title: "Approve calendar scopes", title_bn: "ক্যালেন্ডার স্কোপ অনুমোদন", body: "Grant read + write to your primary calendar only.", body_bn: "প্রাইমারি ক্যালেন্ডারে read + write অনুমতি দিন।" },
      { title: "Pick target calendar", title_bn: "টার্গেট ক্যালেন্ডার নির্বাচন", body: "Choose 'Doctorsx Appointments' or your primary calendar.", body_bn: "'Doctorsx Appointments' বা প্রাইমারি ক্যালেন্ডার বেছে নিন।" },
      { title: "Enable auto-sync", title_bn: "অটো-সিঙ্ক চালু করুন", body: "New bookings appear in Google Calendar within 30 seconds.", body_bn: "নতুন বুকিং ৩০ সেকেন্ডে গুগল ক্যালেন্ডারে আসবে।" },
    ],
  },
  {
    id: "calendly",
    name: "Calendly",
    name_bn: "ক্যালেন্ডলি",
    category: "scheduling",
    icon: <CalendarClock className="h-5 w-5" />,
    tagline: "Let patients self-book from your website or WhatsApp.",
    tagline_bn: "রোগী নিজে ওয়েবসাইট বা হোয়াটসঅ্যাপ থেকে বুক করবে।",
    difficulty: "Easy",
    timeMinutes: 7,
    docsUrl: "https://developer.calendly.com/",
    envVars: [
      { name: "CALENDLY_API_KEY", hint: "Personal Access Token from Calendly", hint_bn: "Calendly থেকে Personal Access Token" },
    ],
    steps: [
      { title: "Create Calendly account", title_bn: "Calendly অ্যাকাউন্ট তৈরি", body: "Free tier is enough for one doctor with one event type.", body_bn: "এক ডাক্তার + এক ইভেন্ট টাইপের জন্য ফ্রি প্ল্যান যথেষ্ট।" },
      { title: "Create an event type", title_bn: "ইভেন্ট টাইপ তৈরি", body: "e.g. 'Consultation — 15 min' with your chamber hours.", body_bn: "যেমন 'Consultation — 15 min' চেম্বার সময় অনুযায়ী।" },
      { title: "Generate API token", title_bn: "API টোকেন জেনারেট", body: "Integrations → API & Webhooks → Personal Access Tokens → Create.", body_bn: "Integrations → API & Webhooks → Personal Access Tokens → Create।" },
      { title: "Paste into Doctorsx", title_bn: "Doctorsx-এ পেস্ট করুন", body: "Settings → Integrations → Calendly. Bookings appear on the Appointments page automatically.", body_bn: "Settings → Integrations → Calendly-এ পেস্ট করুন। বুকিং অ্যাপয়েন্টমেন্ট পেজে চলে আসবে।" },
      { title: "Share your booking link", title_bn: "বুকিং লিংক শেয়ার", body: "Add it to your WhatsApp bio or website 'Book' button.", body_bn: "লিংকটি হোয়াটসঅ্যাপ বায়ো বা ওয়েবসাইটের 'Book' বাটনে দিন।" },
    ],
  },
  {
    id: "resend",
    name: "Resend (Email)",
    name_bn: "Resend (ইমেইল)",
    category: "email",
    icon: <Mail className="h-5 w-5" />,
    tagline: "Transactional emails: receipts, reports, prescription copies.",
    tagline_bn: "ট্রান্সঅ্যাকশনাল ইমেইল: রসিদ, রিপোর্ট, প্রেসক্রিপশন কপি।",
    difficulty: "Easy",
    timeMinutes: 6,
    docsUrl: "https://resend.com/docs",
    envVars: [{ name: "RESEND_API_KEY", hint: "From Resend dashboard → API Keys", hint_bn: "Resend ড্যাশবোর্ড → API Keys থেকে" }],
    steps: [
      { title: "Sign up at resend.com", title_bn: "resend.com-এ সাইন আপ", body: "Free 3,000 emails/month.", body_bn: "প্রতি মাসে ফ্রি ৩,০০০ ইমেইল।" },
      { title: "Add & verify your domain", title_bn: "ডোমেইন যাচাই করুন", body: "Add SPF, DKIM, MX records shown by Resend to your DNS.", body_bn: "Resend-এর দেখানো SPF, DKIM, MX রেকর্ড DNS-এ যোগ করুন।" },
      { title: "Generate API key", title_bn: "API key জেনারেট", body: "API Keys → Create — copy the value (shown only once).", body_bn: "API Keys → Create — মান কপি করুন (একবারই দেখা যায়)।" },
      { title: "Save in Doctorsx", title_bn: "Doctorsx-এ সেভ", body: "Settings → Integrations → Email. Set default From address.", body_bn: "Settings → Integrations → Email-এ ডিফল্ট From অ্যাড্রেস দিন।" },
      { title: "Send test email", title_bn: "টেস্ট ইমেইল পাঠান", body: "Use the tester — confirm delivery in inbox.", body_bn: "টেস্টার দিয়ে পাঠান — ইনবক্সে ডেলিভারি নিশ্চিত করুন।" },
    ],
  },
];

export default function IntegrationsSetup({
  isBn,
  displayFont,
  bodyFont,
}: {
  isBn: boolean;
  displayFont: string;
  bodyFont: string;
}) {
  const [category, setCategory] = useState<"all" | "messaging" | "scheduling" | "email">("all");
  const items = useMemo(
    () => (category === "all" ? INTEGRATIONS : INTEGRATIONS.filter((i) => i.category === category)),
    [category]
  );

  const copy = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast.success(isBn ? `${label} কপি হয়েছে` : `${label} copied`);
  };

  return (
    <section id="integrations" className="mt-20 md:mt-28 scroll-mt-24">
      <div className="text-center mb-10">
        <Badge variant="outline" className="mb-3 border-[#1ba179]/30 text-[#1ba179] bg-[#1ba179]/5">
          {isBn ? "ইন্টিগ্রেশন সেটআপ" : "Integrations Setup"}
        </Badge>
        <h2 className={`${displayFont} text-3xl md:text-5xl font-bold text-[#0b1f3a] tracking-tight`}>
          {isBn ? "যেকোনো টুল যুক্ত করুন — মিনিটে" : "Connect any tool — in minutes"}
        </h2>
        <p className={`${bodyFont} mt-3 max-w-2xl mx-auto text-[#475569] text-base md:text-lg`}>
          {isBn
            ? "মেসেজিং ও শিডিউলিং টুলগুলো ধাপে ধাপে যুক্ত করার গাইড। কোনো কোডিং লাগবে না।"
            : "Step-by-step guides to wire up your messaging and scheduling stack. No coding required."}
        </p>
      </div>

      <Tabs value={category} onValueChange={(v) => setCategory(v as any)} className="w-full">
        <TabsList className="mx-auto flex w-fit bg-[#f1f5f9] border border-[#e2e8f0]">
          <TabsTrigger value="all">{isBn ? "সব" : "All"}</TabsTrigger>
          <TabsTrigger value="messaging">{isBn ? "মেসেজিং" : "Messaging"}</TabsTrigger>
          <TabsTrigger value="scheduling">{isBn ? "শিডিউলিং" : "Scheduling"}</TabsTrigger>
          <TabsTrigger value="email">{isBn ? "ইমেইল" : "Email"}</TabsTrigger>
        </TabsList>

        <TabsContent value={category} className="mt-8">
          <div className="grid md:grid-cols-2 gap-5">
            {items.map((integ) => (
              <Card
                key={integ.id}
                className="p-5 md:p-6 rounded-3xl border border-[#e2e8f0] bg-white/90 backdrop-blur hover:shadow-[0_20px_50px_-30px_rgba(2,132,199,0.35)] transition-shadow"
              >
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="flex items-center gap-3">
                    <div className="h-11 w-11 rounded-2xl bg-gradient-to-br from-[#25c39a] to-[#1ba179] text-white grid place-items-center shadow-lg shadow-[#1ba179]/20">
                      {integ.icon}
                    </div>
                    <div>
                      <div className={`${displayFont} text-lg font-semibold text-[#0b1f3a] leading-tight`}>
                        {isBn ? integ.name_bn : integ.name}
                      </div>
                      <div className={`${bodyFont} text-xs text-[#64748b]`}>
                        {isBn ? integ.tagline_bn : integ.tagline}
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-1 shrink-0">
                    <Badge
                      className={
                        integ.difficulty === "Easy"
                          ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                          : integ.difficulty === "Medium"
                          ? "bg-amber-50 text-amber-700 border border-amber-200"
                          : "bg-rose-50 text-rose-700 border border-rose-200"
                      }
                    >
                      {integ.difficulty}
                    </Badge>
                    <span className="text-[11px] text-[#64748b]">~{integ.timeMinutes} min</span>
                  </div>
                </div>

                <Accordion type="single" collapsible>
                  <AccordionItem value="steps" className="border-b-0">
                    <AccordionTrigger className="py-2 text-sm font-medium text-[#1ba179] hover:no-underline">
                      {isBn ? "ধাপে ধাপে দেখুন" : "View step-by-step guide"}
                    </AccordionTrigger>
                    <AccordionContent>
                      <ol className="space-y-3 mt-2">
                        {integ.steps.map((s, i) => (
                          <li key={i} className="flex gap-3">
                            <div className="shrink-0 h-6 w-6 rounded-full bg-[#1ba179] text-white text-xs font-semibold grid place-items-center">
                              {i + 1}
                            </div>
                            <div className="flex-1">
                              <div className={`${bodyFont} text-sm font-semibold text-[#0b1f3a]`}>
                                {isBn && s.title_bn ? s.title_bn : s.title}
                              </div>
                              <div className={`${bodyFont} text-xs text-[#475569] mt-0.5`}>
                                {isBn && s.body_bn ? s.body_bn : s.body}
                              </div>
                              {s.code && (
                                <button
                                  onClick={() => copy(s.code!, isBn ? "URL" : "URL")}
                                  className="mt-1.5 group inline-flex items-center gap-1.5 rounded-md bg-[#f1f5f9] px-2 py-1 font-mono text-[11px] text-[#0b1f3a] hover:bg-[#e2e8f0] transition"
                                >
                                  <span className="truncate max-w-[240px]">{s.code}</span>
                                  <Copy className="h-3 w-3 opacity-60 group-hover:opacity-100" />
                                </button>
                              )}
                            </div>
                          </li>
                        ))}
                      </ol>

                      {integ.envVars && (
                        <div className="mt-4 rounded-xl bg-[#f8fafc] border border-[#e2e8f0] p-3">
                          <div className={`${bodyFont} text-xs font-semibold text-[#0b1f3a] mb-2 flex items-center gap-1.5`}>
                            <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
                            {isBn ? "প্রয়োজনীয় সিক্রেট" : "Required secrets"}
                          </div>
                          <ul className="space-y-1.5">
                            {integ.envVars.map((v) => (
                              <li key={v.name} className="flex items-center justify-between gap-2">
                                <div>
                                  <code className="font-mono text-[11px] text-[#1ba179]">{v.name}</code>
                                  <div className="text-[11px] text-[#64748b]">{isBn ? v.hint_bn : v.hint}</div>
                                </div>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-7 px-2"
                                  onClick={() => copy(v.name, v.name)}
                                >
                                  <Copy className="h-3 w-3" />
                                </Button>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      <div className="mt-4 flex flex-wrap gap-2">
                        <a href={integ.docsUrl} target="_blank" rel="noreferrer">
                          <Button size="sm" variant="outline" className="border-[#e2e8f0]">
                            <ExternalLink className="h-3.5 w-3.5 mr-1.5" />
                            {isBn ? "অফিসিয়াল ডকস" : "Official docs"}
                          </Button>
                        </a>
                        <a href="/auth">
                          <Button size="sm" className="bg-[#1ba179] hover:bg-[#0f8f6b] text-white">
                            <Send className="h-3.5 w-3.5 mr-1.5" />
                            {isBn ? "এখনই যুক্ত করুন" : "Connect now"}
                          </Button>
                        </a>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      <p className={`${bodyFont} text-center text-xs text-[#64748b] mt-8 max-w-2xl mx-auto`}>
        {isBn
          ? "সব সিক্রেট সার্ভার-সাইডে এনক্রিপ্ট করে রাখা হয়। ব্রাউজারে কখনো এক্সপোজ হয় না।"
          : "All secrets are encrypted server-side and never exposed to the browser."}
      </p>
    </section>
  );
}
